import { PostgrestSingleResponse } from '@supabase/supabase-js';
import { supabase } from '../../../../supabaseConfig';
import { IVwSaldosResponsaveis } from '../../../types/views/IVwSaldosResponsaveis';

/**
 * View para saldos dos responsáveis
 * Corresponde à view 'vw_saldos_responsaveis' no banco de dados
 * Nota: Esta view usa o ID da organização (integer), não o nome
 */
export class ViewSaldosResponsaveis {
     protected client = supabase;
     protected tableName = 'vw_saldos_responsaveis' as const;

     /**
      * Lista todos os saldos dos responsáveis
      */
     async getSaldosResponsaveis(organizationId: number): Promise<PostgrestSingleResponse<IVwSaldosResponsaveis[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq('organizacao', organizationId);
     }

     /**
      * Busca saldo de um responsável específico
      */
     async getSaldoPorResponsavel(organizationId: number, responsavelId: number): Promise<PostgrestSingleResponse<IVwSaldosResponsaveis[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq('organizacao', organizationId)
               .eq('id_responsavel', responsavelId);
     }

     /**
      * Responsáveis com saldo baixo
      */
     async getResponsaveisSaldoBaixo(organizationId: number, limiteMinimo: number = 10): Promise<PostgrestSingleResponse<IVwSaldosResponsaveis[]>> {
          return await this.client
               .from(this.tableName)
               .select('*')
               .eq('organizacao', organizationId)
               .lt('saldo_atual', limiteMinimo)
               .order('saldo_atual', { ascending: true });
     }

     /**
      * Total de saldo de todos os responsáveis
      */
     async getTotalSaldos(organizationId: number): Promise<number> {
          const { data } = await this.client
               .from(this.tableName)
               .select('saldo_atual')
               .eq('organizacao', organizationId);

          if (!data) return 0;
          
          return data.reduce((sum, item) => sum + (Number(item.saldo_atual) || 0), 0);
     }

     /**
      * Estatísticas dos saldos
      */
     async getEstatisticasSaldos(organizationId: number): Promise<{ sum: number; avg: number; max: number; min: number }> {
          const { data } = await this.client
               .from(this.tableName)
               .select('saldo_atual')
               .eq('organizacao', organizationId);

          if (!data || data.length === 0) {
               return { sum: 0, avg: 0, max: 0, min: 0 };
          }

          const valores = data.map(item => Number(item.saldo_atual) || 0);
          const sum = valores.reduce((acc, val) => acc + val, 0);
          const avg = sum / valores.length;
          const max = Math.max(...valores);
          const min = Math.min(...valores);

          return { sum, avg, max, min };
     }
}

export const viewSaldosResponsaveis = new ViewSaldosResponsaveis();
